import React, { useState } from 'react';
import { changePassword } from '../../services/api';
import { Button } from '../ui/button';
import { Key, Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const PasswordManagement = () => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 6,
      hasLetter: /[a-zA-Z]/.test(password),
      hasNumber: /\d/.test(password),
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.new_password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (formData.current_password === formData.new_password) {
      toast.error('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      await changePassword({
        current_password: formData.current_password,
        new_password: formData.new_password,
      });
      toast.success('Password changed successfully');
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">
          Change Password
        </h1>
        <p className="text-gray-400 mt-2">Update your account password</p>
      </div>

      {/* Password Change Form */}
      <div className="bg-black/40 backdrop-blur-sm rounded-lg border border-orange-500/30 p-8">
        <div className="flex items-center space-x-3 mb-6 pb-6 border-b border-orange-500/20">
          <div className="p-3 bg-gradient-to-br from-orange-500/20 to-green-500/20 rounded-lg">
            <Lock className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Security Settings</h2>
            <p className="text-sm text-gray-400">Keep your account secure with a strong password</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Password *
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.current_password}
                onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                className="w-full px-4 py-3 bg-black/50 border border-orange-500/30 rounded-lg text-white focus:outline-none focus:border-orange-500 pr-10"
                required
                placeholder="Enter your current password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Password *
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                className="w-full px-4 py-3 bg-black/50 border border-orange-500/30 rounded-lg text-white focus:outline-none focus:border-orange-500 pr-10"
                required
                minLength="6"
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Password Requirements */}
            {formData.new_password && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-400">Password requirements:</p>
                <div className="space-y-1">
                  <div className={`flex items-center text-xs ${
                    passwordRequirements.length ? 'text-green-400' : 'text-gray-400'
                  }`}>
                    <CheckCircle className={`w-3 h-3 mr-2 ${
                      passwordRequirements.length ? 'text-green-400' : 'text-gray-500'
                    }`} />
                    At least 6 characters
                  </div>
                  <div className={`flex items-center text-xs ${
                    passwordRequirements.hasLetter ? 'text-green-400' : 'text-gray-400'
                  }`}>
                    <CheckCircle className={`w-3 h-3 mr-2 ${
                      passwordRequirements.hasLetter ? 'text-green-400' : 'text-gray-500'
                    }`} />
                    Contains letters
                  </div>
                  <div className={`flex items-center text-xs ${
                    passwordRequirements.hasNumber ? 'text-green-400' : 'text-gray-400'
                  }`}>
                    <CheckCircle className={`w-3 h-3 mr-2 ${
                      passwordRequirements.hasNumber ? 'text-green-400' : 'text-gray-500'
                    }`} />
                    Contains numbers
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password *
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full px-4 py-3 bg-black/50 border border-orange-500/30 rounded-lg text-white focus:outline-none focus:border-orange-500 pr-10"
                required
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {formData.new_password && formData.confirm_password && (
              <p className={`text-xs mt-1 ${
                formData.new_password === formData.confirm_password
                  ? 'text-green-400'
                  : 'text-red-400'
              }`}>
                {formData.new_password === formData.confirm_password
                  ? '✓ Passwords match'
                  : '✗ Passwords do not match'
                }
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Key className="w-4 h-4 mr-2" />
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </div>
        </form>

        {/* Security Tips */}
        <div className="mt-8 pt-6 border-t border-orange-500/20">
          <h3 className="text-sm font-semibold text-orange-400 mb-3">Security Tips</h3>
          <ul className="space-y-2 text-sm text-gray-400">
            <li className="flex items-start">
              <span className="text-orange-400 mr-2">•</span>
              Use a strong, unique password that you don't use anywhere else
            </li>
            <li className="flex items-start">
              <span className="text-orange-400 mr-2">•</span>
              Avoid using personal information in your password
            </li>
            <li className="flex items-start">
              <span className="text-orange-400 mr-2">•</span>
              Consider using a password manager to generate and store secure passwords
            </li>
            <li className="flex items-start">
              <span className="text-orange-400 mr-2">•</span>
              Change your password regularly to maintain account security
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};